/**
 * LaGift — related products
 * ---------------------------------------------------------------------------
 * Shopify only fills `recommendations.products` when the section is requested
 * through the recommendations endpoint, so the page renders placeholders and
 * this fetches the real row and swaps it in.
 *
 * It replaces its own children rather than the element, which matters: the
 * element is what the theme editor re-renders and what a second call would find
 * again. Replacing the element would leave nothing behind to fetch with.
 *
 * The row that arrives contains `<lagift-drag-scroll>` and `<lagift-quick-add>`.
 * Both upgrade by themselves when inserted - `connectedCallback` runs on
 * insertion, not only at parse - so nothing here has to know they exist.
 */
/**
 * Scoped so the file can run more than once. A `<script src>` executes once per
 * tag, not once per URL, and a top-level class throws "already been declared"
 * on the second run. See CLAUDE.md.
 */
(() => {
  class LagiftRelatedProducts extends HTMLElement {
    connectedCallback() {
      // The editor re-renders this element on every settings change, and each
      // render would otherwise fire another request.
      if (this.loaded) return;
      this.loaded = true;

      const url = this.dataset.url;
      if (!url) return;

      // Nothing below the fold should compete with the product's own images.
      // The row is fetched when it is close to being seen.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load(url);
          },
          { rootMargin: '400px' }
        );
        this.observer.observe(this);
        return;
      }

      this.load(url);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) return this.giveUp();

        const markup = await response.text();
        const fetched = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('lagift-related-products');

        // A store with no recommendations and no fallback returns the section
        // with nothing in it. Removing the band is better than leaving a
        // heading over a row of placeholders that will never be filled.
        if (!fetched || fetched.children.length === 0) return this.giveUp();

        this.replaceChildren(...fetched.children);
      } catch (error) {
        // Offline, or the endpoint refused. The placeholders are not worth
        // showing on their own.
        this.giveUp();
      }
    }

    giveUp() {
      this.remove();
    }
  }

  if (!customElements.get('lagift-related-products')) {
    customElements.define('lagift-related-products', LagiftRelatedProducts);
  }
})();
